<template>
    <page-container>
        <page-header>
            Dashboard
            <template v-slot:actions>
                <analytics-controls v-model="controls" :orgUnitId="props.orgUnitId" :groupId="props.groupId" />
            </template>
        </page-header>

        <div class="grid gap-6 grid-cols-1 lg:grid-cols-12">
            <template v-if="props.orgUnitId || props.groupId">
                <analytics-contacts-total-created
                    v-if="props.groupId"
                    class="lg:col-span-3"
                    :startDate="controls.start_date"
                    :endDate="controls.end_date"
                    :groupId="props.groupId"
                    type="affiliated"
                >
                    <template v-slot:title>New Affiliated Contacts</template>
                    <template v-slot:explainHeader>New Affiliated Contacts</template>
                    <template v-slot:explainContent>
                        <p>
                            <strong>New Affiliated Contacts</strong><br />
                            Gross Increase Count of Contacts that are at least affiliated with your location or
                            department during the selected period.
                        </p>

                        <p>
                            <strong>New Affiliated Contacts %</strong><br />
                            Gross increase of Contacts that are at least affiliated with your location or department
                            during the selected period relative to the previous period.
                        </p>
                        <vx-button href="/contacts" size="sm" color="muted">
                            Go to Contacts
                            <font-awesome-icon widthAuto :icon="faArrowRight"></font-awesome-icon>
                        </vx-button>
                    </template>
                </analytics-contacts-total-created>
                <analytics-contacts-total-created
                    :class="props.orgUnitId ? 'lg:col-span-4' : 'lg:col-span-3'"
                    :startDate="controls.start_date"
                    :endDate="controls.end_date"
                    :orgUnitId="props.orgUnitId"
                    :groupId="props.groupId"
                    type="preferred"
                >
                    <template v-slot:title>New Preferred Contacts</template>
                    <template v-slot:explainHeader>New Preferred Contacts</template>
                    <template v-slot:explainContent>
                        <p>
                            <strong>New Preferred Contacts</strong><br />
                            Gross Increase Count of Contacts that have a unique relation with your location or
                            department during the selected period.
                        </p>

                        <p>
                            <strong>New Preferred Contacts %</strong><br />
                            Gross increase of Contacts that have a unique relation with your location or department
                            during the selected period relative to the previous period.
                        </p>
                        <vx-button href="/contacts" size="sm" color="muted">
                            Go to Contacts
                            <font-awesome-icon widthAuto :icon="faArrowRight"></font-awesome-icon>
                        </vx-button>
                    </template>
                </analytics-contacts-total-created>

                <analytics-marketing-subscriptions-total-opt-ins
                    :class="props.orgUnitId ? 'lg:col-span-4' : 'lg:col-span-3'"
                    :startDate="controls.start_date"
                    :endDate="controls.end_date"
                    :orgUnitId="props.orgUnitId"
                    :groupId="props.groupId"
                    type="preferred"
                >
                    <template v-slot:title>New Preferred Subscribers</template>
                    <template v-slot:explainHeader>New Preferred Subscribers</template>
                    <template v-slot:explainContent>
                        <p>
                            <strong>New Preferred Subscribers</strong><br />
                            Gross Increase Count of Preferred Contacts that consented to receive marketing messages,
                            during the selected period.
                        </p>

                        <p>
                            <strong>New Preferred Subscribers %</strong><br />
                            Gross Increase Percent of Preferred Contacts that consented to receive marketing messages,
                            during the selected period relative to the previous period.
                        </p>
                        <vx-button href="/contacts" size="sm" color="muted">
                            Go to Contacts
                            <font-awesome-icon widthAuto :icon="faArrowRight"></font-awesome-icon>
                        </vx-button>
                    </template>
                </analytics-marketing-subscriptions-total-opt-ins>

                <analytics-marketing-subscriptions-total-opt-outs
                    :class="props.orgUnitId ? 'lg:col-span-4' : 'lg:col-span-3'"
                    :startDate="controls.start_date"
                    :endDate="controls.end_date"
                    :orgUnitId="props.orgUnitId"
                    :groupId="props.groupId"
                    type="preferred"
                >
                    <template v-slot:title>New Preferred Unsubscribers</template>
                    <template v-slot:explainHeader>New Preferred Unsubscribers</template>
                    <template v-slot:explainContent>
                        <p>
                            <strong>New Preferred Unsubscribers</strong><br />
                            Gross Increase Count of Preferred Contacts that have explicitly denied receiving marketing
                            messages, during the selected period.
                        </p>

                        <p>
                            <strong>New Preferred Unsubscribers %</strong><br />
                            Gross Increase Percent of Preferred Contacts that have explicitly denied receiving marketing
                            messages, during the selected period relative to the previous period.
                        </p>
                        <vx-button href="/contacts" size="sm" color="muted">
                            Go to Contacts
                            <font-awesome-icon widthAuto :icon="faArrowRight"></font-awesome-icon>
                        </vx-button>
                    </template>
                </analytics-marketing-subscriptions-total-opt-outs>
            </template>
            <template v-else>
                <analytics-contacts-total-created
                    class="lg:col-span-4"
                    :startDate="controls.start_date"
                    :endDate="controls.end_date"
                >
                </analytics-contacts-total-created>

                <analytics-marketing-subscriptions-total-opt-ins
                    class="lg:col-span-4"
                    :startDate="controls.start_date"
                    :endDate="controls.end_date"
                >
                </analytics-marketing-subscriptions-total-opt-ins>

                <analytics-marketing-subscriptions-total-opt-outs
                    class="lg:col-span-4"
                    :startDate="controls.start_date"
                    :endDate="controls.end_date"
                >
                </analytics-marketing-subscriptions-total-opt-outs>
            </template>

            <analytics-outbound-messages-total-messages-sent-by-audience-type
                class="lg:col-span-12"
                :fetchParams="{ range: [controls.start_date, controls.end_date] }"
                :orgUnitId="props.orgUnitId"
                :groupId="props.groupId"
            />

            <analytics-outbound-messages-total-contacts-messaged
                class="lg:col-span-3"
                :fetchParams="{ range: [controls.start_date, controls.end_date] }"
                :orgUnitId="props.orgUnitId"
                :groupId="props.groupId"
            >
            </analytics-outbound-messages-total-contacts-messaged>

            <analytics-outbound-messages-total-messages-sent-by-message-type
                class="lg:col-span-3"
                :fetchParams="{ range: [controls.start_date, controls.end_date] }"
                :orgUnitId="props.orgUnitId"
                :groupId="props.groupId"
            >
            </analytics-outbound-messages-total-messages-sent-by-message-type>

            <analytics-outbound-messages-total-messages-sent-by-message-type
                class="lg:col-span-3"
                messageType="sms"
                :fetchParams="{ range: [controls.start_date, controls.end_date] }"
                :orgUnitId="props.orgUnitId"
                :groupId="props.groupId"
            >
            </analytics-outbound-messages-total-messages-sent-by-message-type>

            <analytics-outbound-messages-total-messages-sent-by-message-type
                class="lg:col-span-3"
                messageType="mms"
                :fetchParams="{ range: [controls.start_date, controls.end_date] }"
                :orgUnitId="props.orgUnitId"
                :groupId="props.groupId"
            >
            </analytics-outbound-messages-total-messages-sent-by-message-type>

            <analytics-outbound-messages-delivery-rate
                class="lg:col-span-6"
                :fetchParams="{ range: [controls.start_date, controls.end_date] }"
                :orgUnitId="props.orgUnitId"
                :groupId="props.groupId"
            >
            </analytics-outbound-messages-delivery-rate>

            <analytics-outbound-messages-message-reply-rate
                class="lg:col-span-6"
                :fetchParams="outboundMessagesMarketingCampaignsParams"
                :orgUnitId="props.orgUnitId"
                :groupId="props.groupId"
            >
                <template v-slot:title> Marketing Blast Campaign Message Reply Rate </template>
            </analytics-outbound-messages-message-reply-rate>

            <analytics-outbound-messages-total-messages-sent-by-resource
                rateType="message-reply"
                :fetchParams="outboundMessagesMarketingCampaignsParams"
                :orgUnitId="props.orgUnitId"
                :groupId="props.groupId"
                class="lg:col-span-6"
            >
                <template v-slot:title> Messages Sent per Marketing Blast Campaign </template>
                <template v-slot:explainHeader> Messages Sent per Marketing Blast Campaign </template>
                <template v-slot:explainContent>
                    <p>
                        <strong>Messages Sent</strong>: Gross Count of Messages sent by each Marketing Blast Campaign
                        during the selected period.
                    </p>
                </template>
            </analytics-outbound-messages-total-messages-sent-by-resource>

            <analytics-outbound-messages-rate-type-by-resource
                :rateType="showUniqueClickThroughRate ? 'unique_click_through_rate' : 'click_through_rate'"
                :fetchParams="outboundMessagesMarketingCampaignsParams"
                :orgUnitId="props.orgUnitId"
                :groupId="props.groupId"
                class="lg:col-span-6"
            >
                <template v-slot:title>
                    {{ showUniqueClickThroughRate ? 'Unique' : '' }} Click-Through Rate per Marketing Blast Campaign
                </template>
                <template v-slot:explainHeader>
                    {{ showUniqueClickThroughRate ? 'Unique' : '' }} Click-Through Rate per Marketing Blast Campaign
                </template>
                <template v-slot:explainContent>
                    <p>
                        <strong>{{ showUniqueClickThroughRate ? 'Unique' : '' }} Click-Through Rate</strong>: In the
                        Context of Marketing Blast Campaigns, in particular the ones that provide a clickable link; this
                        metric allows you to evaluate the success through the
                        {{ showUniqueClickThroughRate ? 'unique' : 'relative' }} clicks a campaigns receives relative to
                        the quantity of messages delivered.
                    </p>
                </template>
            </analytics-outbound-messages-rate-type-by-resource>
        </div>
    </page-container>
</template>

<script setup>
import { ref, computed } from 'vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageContainer from '~/components/general/PageContainer.vue';
import AnalyticsControls from './partial/AnalyticsControls.vue';
import AnalyticsMarketingSubscriptionsTotalOptIns from './charts/time-series/AnalyticsMarketingSubscriptionsTotalOptIns.vue';
import AnalyticsMarketingSubscriptionsTotalOptOuts from './charts/time-series/AnalyticsMarketingSubscriptionsTotalOptOuts.vue';
import AnalyticsContactsTotalCreated from './charts/time-series/AnalyticsContactsTotalCreated.vue';
import AnalyticsOutboundMessagesTotalContactsMessaged from './charts/time-series/AnalyticsOutboundMessagesTotalContactsMessaged.vue';
import AnalyticsOutboundMessagesTotalMessagesSentByMessageType from './charts/time-series/AnalyticsOutboundMessagesTotalMessagesSentByMessageType.vue';
import AnalyticsOutboundMessagesDeliveryRate from './charts/time-series/AnalyticsOutboundMessagesDeliveryRate.vue';
import AnalyticsOutboundMessagesMessageReplyRate from './charts/time-series/AnalyticsOutboundMessagesMessageReplyRate.vue';
import AnalyticsOutboundMessagesRateTypeByResource from './charts/time-series/AnalyticsOutboundMessagesRateTypeByResource.vue';
import { controlDateRangeOptions, minUniqueClickThroughRateDate } from '../../utils/analytics';
import AnalyticsOutboundMessagesTotalMessagesSentByResource from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesTotalMessagesSentByResource.vue';
import AnalyticsOutboundMessagesTotalMessagesSentByAudienceType from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesTotalMessagesSentByAudienceType.vue';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { VxButton } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const props = defineProps({
    orgUnitId: {
        type: Number,
    },
    groupId: {
        type: String,
    },
});

const controls = ref(
    controlDateRangeOptions(Boolean(props.groupId), Boolean(props.orgUnitId)).find(
        (option) => option.value === 'last_30_days'
    )
);

const outboundMessagesMarketingCampaignsParams = computed(() => ({
    range: [controls.value.start_date, controls.value.end_date],
    filters: {
        audience_types: ['marketing'],
        resource_types: ['one_time_campaign'],
    },
}));

const showUniqueClickThroughRate = computed(() =>
    minUniqueClickThroughRateDate.isBefore(outboundMessagesMarketingCampaignsParams.value.range[0])
);
</script>
